import { installedItems } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { GitlabIconUrl } from "./icon.ts";
import { gitlabMcpTools } from "./mcp-tools.ts";
import type { GitlabAiIntegration } from "./types.ts";

// Measured on GitLab.com: a new token and a membership change both worked on
// the next call.
const gitlabPropagationHint = propagationHint({
  resource: "GitLab",
  normalWait: "5 to 15 seconds",
  maxWait: "1 minute",
  pollInterval: "5 seconds",
});

const prefixArgs = ["gitlab-ai", "permission"];

const accessPrompt = `Request access to GitLab projects by calling with argv:

  [<group>, <project>:<role>..., "--reason", <reason>]

where:

  - group: The GitLab top-level group, as installed in P0
  - project: The project's full path (e.g. 'acme/app'). Use the list tool to
    enumerate them.
  - role: One of 'guest', 'reporter', 'developer' or 'maintainer'. Ask for the
    lowest role that the tools you intend to call need: 'reporter' reads code
    and merge requests, 'developer' also pushes and opens merge requests.
  - reason: The reason access is needed

Every tool is listed whether or not your access covers it; GitLab refuses a
call on a project that your access does not cover.
`;

const gitlabMcp: McpServerSpec & { type: "external" } = {
  type: "external",
  label: "GitLab",
  iconUrl: GitlabIconUrl,
  credential: {
    type: "vault",
    // Each grant stages its own secret name; read that rather than rebuilding
    // the naming scheme here.
    path: "{{ request.generated.secretName }}",
  },
  tools: { type: "manual", tools: gitlabMcpTools },
  // The service account's project memberships decide what GitLab allows.
  authorization: "upstream",
  url: "https://gitlab.com/api/v4/mcp",
  access: {
    prompt: accessPrompt,
    prefixArgs,
    propagationHint: gitlabPropagationHint,
  },
  list: {
    fields: {
      group: {
        description: "GitLab top-level group",
        required: [],
      },
      policies: {
        description: "Project and role that a grant can give, for one group",
        required: ["group"],
      },
    },
    prefixArgs,
  },
};

export const gitlabAiMcpServers = { gitlab: gitlabMcp };

/** Whether {@link gitlabAiMcpServers} can be added: only when a GitLab group
 * is installed. */
export const gitlabAiMcpServerAvailability = {
  gitlab: (installed: Record<string, NewConfig>): boolean =>
    Object.keys(
      installedItems(
        "credential",
        (installed["gitlab-ai"] ?? {}) as GitlabAiIntegration
      )
    ).length > 0,
};
